import { motion } from 'framer-motion';
import { Link } from 'wouter';
import { BarChart3, Check, ExternalLink } from 'lucide-react';
import { PollAPI } from '@shared/types/api';
import { useSubmitPollResponse } from '@/lib/api';
import { useToast } from '@/components/ux/Toast';
import tracking from '@/lib/tracking';
import Markdown from '@/components/Markdown';
import { hasRichAnswers } from '@/lib/polls';
import { PollOptionCards } from './PollOptionCards';

interface PollCardProps {
  poll: PollAPI.UserPoll;
}

export function PollCard({ poll }: PollCardProps) {
  const submitResponse = useSubmitPollResponse();
  const toast = useToast();

  const hasVoted = poll.hasResponded;
  const showResults = poll.publicResults && poll.results;

  const handleSelect = (answerId: string) => {
    // If already voted for this option, do nothing
    if (answerId === poll.userResponse) return;

    // Submit immediately
    submitResponse.mutate(
      { pollId: poll.id, answerId },
      {
        onSuccess: () => {
          toast.success(hasVoted ? 'Vote updated!' : 'Vote submitted!');
          tracking.pollVote(poll.id);
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.error || 'Failed to submit vote');
        },
      }
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card"
    >
      {/* Poll Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center flex-shrink-0">
          <BarChart3 className="w-5 h-5 text-primary-500" />
        </div>
        <div className="flex-1">
          {/* The question is markdown (may contain its own links), so it can't sit inside the Link. */}
          <div className="flex items-start gap-2">
            <Markdown size="lg" className="font-bold flex-1 min-w-0 break-words">{poll.question}</Markdown>
            <Link
              href={`/poll/${poll.id}`}
              aria-label="Open poll"
              className="mt-1.5 text-dark-muted hover:text-primary-400 transition-colors flex-shrink-0"
            >
              <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
          {poll.totalResponses !== null && (
            <p className="text-sm text-dark-muted">
              {poll.totalResponses} {poll.totalResponses === 1 ? 'vote' : 'votes'}
            </p>
          )}
        </div>
      </div>

      {/* Answers — rich option cards when options carry images/details */}
      {hasRichAnswers(poll.answers) ? (
        <PollOptionCards poll={poll} onSelect={handleSelect} isPending={submitResponse.isPending} />
      ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 auto-rows-auto">
        {poll.answers.map((answer) => {
          const result = poll.results?.find(r => r.answerId === answer.id);
          const isUserVote = poll.userResponse === answer.id;

          // Show results view with clickable options (when public results enabled)
          if (showResults && result) {
            return (
              <button
                key={answer.id}
                onClick={() => handleSelect(answer.id)}
                disabled={submitResponse.isPending}
                className={`w-full text-left relative transition-all ${submitResponse.isPending ? 'opacity-50' : 'cursor-pointer'}`}
              >
                <div
                  className={`relative z-10 px-4 py-3 rounded-lg border transition-colors ${
                    isUserVote
                      ? 'border-primary-500 bg-primary-500/10'
                      : 'border-type-200 hover:border-primary-500/50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="font-medium truncate">{answer.text}</span>
                      {isUserVote && (
                        <Check className="w-4 h-4 text-primary-500 flex-shrink-0" />
                      )}
                    </div>
                    <span className="text-sm text-dark-muted flex-shrink-0">
                      {result.percentage.toFixed(0)}%
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-2 h-1.5 bg-dark-border rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${result.percentage}%` }}
                      transition={{ duration: 0.5, delay: 0.1 }}
                      className={`h-full rounded-full ${
                        isUserVote ? 'bg-primary-500' : 'bg-primary-500/50'
                      }`}
                    />
                  </div>
                </div>
              </button>
            );
          }

          // Voting view (no results shown)
          return (
            <button
              key={answer.id}
              onClick={() => handleSelect(answer.id)}
              disabled={submitResponse.isPending}
              className={`w-full px-4 py-3 rounded-lg border text-left transition-all ${
                isUserVote
                  ? 'border-primary-500 bg-primary-500/10 ring-1 ring-primary-500/50'
                  : 'border-type-200 hover:border-primary-500/50'
              } ${submitResponse.isPending ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                    isUserVote
                      ? 'border-primary-500 bg-primary-500'
                      : 'border-type-200'
                  }`}
                >
                  {isUserVote && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="font-medium truncate">{answer.text}</span>
              </div>
            </button>
          );
        })}
      </div>
      )}

      {/* Loading indicator */}
      {submitResponse.isPending && (
        <p className="text-sm text-dark-muted text-center mt-4">
          Saving...
        </p>
      )}
    </motion.div>
  );
}

